import { useContext } from "react";
import css from "../Food.module.css";
import Intro from "../../../components/intro/Intro";
import NotificationsContext from "../../../context/notifications";
import Gallery from "../../../components/gallery/Gallery";
import Steps from "../Steps";
import Clickard from "../../../components/Clickard/Clickard";

const BananaBread = () => {
  const { addNotification } = useContext(NotificationsContext);

  return (
    <>
      <Intro
        primary="BANANA BREAD"
        secondary={`You'll need 90 minutes (30 to prepare, 60 to bake), 8 ingredients, and 7 tools. Don't be intimidated—this is a simple and delicious recipe. In fact, it's so worth the effort that you might find yourself making it a little too often.`}
        tertiary="Enjoy!"
      />
      <div className="column p01 g1">
        <Gallery
          pictures={[
            {
              src: "https://storage.googleapis.com/squidslippers-media/food/banana_bread/setup.jpg",
              alt: "The tools and ingredients required to make this recipe of banana bread. Tools: Oven (blurred in the background), bowl (large), bowl (medium), 9x5-inch loaf pan, 1 cup, 1/2 teaspoon, fork. Ingredients: 3 very ripe bananas, 8 tablespoons (1 stick) of butter, 2 eggs, all-purpose flour, salt, baking powder, sugar, vanilla extract.",
            },
            {
              src: "https://storage.googleapis.com/squidslippers-media/food/banana_bread/loaf.jpg",
              alt: "An uncut loaf of banana bread on a gray countertop, shot from directly above. It is cooked to perfection (if I may say so myself.)",
            },
            {
              src: "https://storage.googleapis.com/squidslippers-media/food/banana_bread/plate.jpg",
              alt: "A stylized photo of banana bread, shot from above and divided into quadrants: half a loaf of banana bread (top left), a glass of milk (top right), a plate with a slice of bread on it (bottom right), and a few slices of bread resting on the counter (bottom left).",
            },
          ]}
        />

        <h2 style={{ marginTop: "3rem" }}>1. PREPARE</h2>
        <div className="columns_2">
          <div className={css.instruction}>
            <strong className="ctext">INGREDIENTS</strong>
            <li className="flex between">
              <span className="white">very ripe bananas</span> 3
            </li>
            <li className="flex between">
              <span className="white">butter</span>8 tablespoons
            </li>
            <li className="flex between">
              <span className="white">eggs</span>2
            </li>
            <li className="flex between">
              <span className="white">flour (all-purpose)</span>2 cups
            </li>
            <li className="flex between">
              <span className="white">salt</span>½ teaspoon
            </li>
            <li className="flex between">
              <span className="white">baking powder</span>1½ teaspoon
            </li>
            <li className="flex between">
              <span className="white">sugar</span>1 cup
            </li>
            <li className="flex between">
              <span className="white">vanilla extract</span>1 teaspoon
            </li>
          </div>

          <div className={css.instruction}>
            <strong className="ctext">TOOLS</strong>
            <div className="flex between">
              <span className="underline">oven</span>1
            </div>
            <div className="flex between">
              <span className="underline">bowl (large)</span>1
            </div>
            <div className="flex between">
              <span className="underline">bowl (medium)</span>1
            </div>
            <div className="flex between">
              <span className="underline">9x5-inch loaf pan</span>1
            </div>
            <div className="flex between">
              <span className="underline">1 cup</span>1
            </div>
            <div className="flex between">
              <span className="underline">½ teaspoon</span>1
            </div>
            <div className="flex between">
              <span className="underline">fork</span>1
            </div>
          </div>
        </div>

        <h2>2. COOK</h2>
        <div className="column g1">
          <Steps
            img={{
              src: "https://storage.googleapis.com/squidslippers-media/food/banana_bread/butter_in_pan.jpg",
              alt: "placeholder",
            }}
          >
            <p>
              Preheat the <span className="underline">oven</span> to 350º.
            </p>
            <p>
              Apply a thin layer of <span className="white">butter</span> to the{" "}
              <span className="underline">loaf pan</span>.
            </p>
          </Steps>
          <Steps
            img={{
              src: "https://storage.googleapis.com/squidslippers-media/food/banana_bread/dry_ingredients_in_bowl.jpg",
              alt: "placeholder",
            }}
          >
            <p>
              Add to the <span className="underline">large bowl</span>...
            </p>
            <ul className={css.bullets}>
              <li className="flex between">
                <span className="white">flour</span> 2 cups
              </li>
              <li className="flex between">
                <span className="white">salt</span> ½ teaspoon
              </li>
              <li className="flex between">
                <span className="white">baking powder</span> 1½ teaspoon
              </li>
              <li className="flex between">
                <span className="white">sugar</span> 1 cup
              </li>
            </ul>
            <p>
              ...then mix together with the{" "}
              <span className="underline">fork</span>.
            </p>
          </Steps>
          <Steps
            img={{
              src: "https://storage.googleapis.com/squidslippers-media/food/banana_bread/wet_ingredients_in_bowl.jpg",
              alt: "placeholder",
            }}
          >
            <p>
              Add to the <span className="underline">medium bowl</span>...
            </p>
            <ul className={css.bullets}>
              <li className="flex between">
                <span className="white">bananas</span>3
              </li>
              <li className="flex between">
                <span className="white">butter</span>8 tablespoons
              </li>
              <li className="flex between">
                <span className="white">eggs</span>2
              </li>
              <li className="flex between">
                <span className="white">vanilla</span>1 teaspoon
              </li>
            </ul>
            <p>
              ...then mash with the <span className="underline">fork</span>{" "}
              until smooth.
            </p>
          </Steps>
          <Steps
            img={{
              src: "https://storage.googleapis.com/squidslippers-media/food/banana_bread/mixture_in_bowl.jpg",
              alt: "placeholder",
            }}
          >
            <p>
              Pour the contents of the{" "}
              <span className="underline">medium bowl</span> into the{" "}
              <span className="underline">large bowl</span>...
            </p>
            <p>
              ...then mix together with the{" "}
              <span className="underline">fork</span> until the dry ingredients
              are all wet.
            </p>
          </Steps>
          <Steps
            img={{
              src: "https://storage.googleapis.com/squidslippers-media/food/banana_bread/mixture_in_pan.jpg",
              alt: "placeholder",
            }}
          >
            <p>
              Pour the mixture into the{" "}
              <span className="underline">loaf pan</span>
              ...
            </p>
            <p>
              ...then put the <span className="underline">loaf pan</span> into
              the <span className="underline">oven</span>.
            </p>
          </Steps>
          <p className="ctext white">Wait 50–60 minutes. (Set a timer.)</p>
          <Steps
            img={{
              src: "https://storage.googleapis.com/squidslippers-media/food/banana_bread/loaf_in_pan.jpg",
              alt: "placeholder",
            }}
          >
            <p>
              Check for doneness by poking the top of the{" "}
              <span className="white">banana bread</span> with the{" "}
              <span className="underline">fork</span>: if there's no wet
              batter...
            </p>
            <p>
              ...remove the <span className="white">banana bread</span> from the{" "}
              <span className="underline">oven</span>.
            </p>
          </Steps>
          <button
            className="button"
            onClick={() =>
              addNotification({
                primary: "NICE WORK!",
                secondary: "You did it! Enjoy!",
                type: "success",
                showTime: 4,
              })
            }
          >
            CELEBRATE!
          </button>
        </div>
        <h2>MORE TO COOK</h2>
        <div className="columns_3">
          <Clickard
            img={{
              src: "https://storage.googleapis.com/squidslippers-media/food/pancakes.webp",
              alt: "pancakes",
            }}
            link="/cookbook/pancakes"
            text="Pancakes"
          />
          <Clickard
            img={{
              src: "https://storage.googleapis.com/squidslippers-media/food/popcorn.jpg",
              alt: "popcorn",
            }}
            link="/cookbook/popcorn"
            text="Popcorn"
          />
        </div>
      </div>
    </>
  );
};

export default BananaBread;
